// Per-page renderer for "comparative" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, formatValue, fetchPageAI } from '../utils';
import { drawBarChart } from '../charts';

/**
 * Compute base vs comp date windows matching what the ComparativeAnalysis page
 * uses on initial load (Year-over-Year with fiscal-year alignment). This keeps
 * the report content in parity with what the user sees by default on the UI.
 */
function computeYoYWindows(fiscalStartMonth, latestDataDate) {
  const ref = latestDataDate ? new Date(latestDataDate) : new Date();
  function fiscalYearRange(refDate) {
    const y = refDate.getFullYear();
    const m = refDate.getMonth() + 1;
    let fyStart; let fyEnd;
    if (fiscalStartMonth === 1) {
      fyStart = new Date(y, 0, 1); fyEnd = new Date(y, 11, 31);
    } else if (m >= fiscalStartMonth) {
      fyStart = new Date(y, fiscalStartMonth - 1, 1);
      fyEnd = new Date(y + 1, fiscalStartMonth - 1, 0);
    } else {
      fyStart = new Date(y - 1, fiscalStartMonth - 1, 1);
      fyEnd = new Date(y, fiscalStartMonth - 1, 0);
    }
    return { fyStart, fyEnd };
  }
  // Most recent complete FY = base; the FY before that = comp.
  const currentFY = fiscalYearRange(ref);
  const prevRef = new Date(currentFY.fyStart); prevRef.setFullYear(prevRef.getFullYear() - 1);
  const prevFY = fiscalYearRange(prevRef);
  const olderRef = new Date(prevFY.fyStart); olderRef.setFullYear(olderRef.getFullYear() - 1);
  const olderFY = fiscalYearRange(olderRef);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return {
    base_start: fmt(prevFY.fyStart),
    base_end: fmt(prevFY.fyEnd),
    comp_start: fmt(olderFY.fyStart),
    comp_end: fmt(olderFY.fyEnd),
    base_label: `FY ${prevFY.fyStart.getFullYear()}${fiscalStartMonth !== 1 ? `-${String(prevFY.fyEnd.getFullYear()).slice(-2)}` : ''}`,
    comp_label: `FY ${olderFY.fyStart.getFullYear()}${fiscalStartMonth !== 1 ? `-${String(olderFY.fyEnd.getFullYear()).slice(-2)}` : ''}`,
  };
}

export async function renderComparative(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  // Mirror the UI: look up fiscal calendar + latest data date, then YoY preset.
  let fiscalStartMonth = 1;
  let latestDate = null;
  try {
    const [fRes, rRes] = await Promise.all([
      api.get('/settings/fiscal-calendar').catch(() => ({ data: {} })),
      api.get('/analytics/data-date-range').catch(() => ({ data: {} })),
    ]);
    fiscalStartMonth = fRes.data?.fiscal_year_start_month || 1;
    latestDate = rRes.data?.max_date || null;
  } catch {}
  const w = computeYoYWindows(fiscalStartMonth, latestDate);

  // Build the full querystring: global filters + YoY date windows.
  const params = new URLSearchParams(qs);
  params.set('base_start', w.base_start);
  params.set('base_end', w.base_end);
  params.set('comp_start', w.comp_start);
  params.set('comp_end', w.comp_end);

  const res = await api.get(`/analytics/comparative?${params.toString()}`);
  const d = res.data;
  const summary = d.summary || {};
  const periodLabel = `${w.base_label} (base) vs ${w.comp_label} (comp)`;

  const entityLines = (d.by_entity || []).map(e => `- ${e.entity}: base=${formatValue(e.base_spend || 0)}, comp=${formatValue(e.comp_spend || 0)}, delta=${e.delta_pct || 0}%`).join('\n');
  const sbuLines = (d.by_sbu || []).map(s => `- ${s.sbu}: base=${formatValue(s.base_spend || 0)}, comp=${formatValue(s.comp_spend || 0)}, delta=${s.delta_pct || 0}%`).join('\n');
  const catLines = (d.by_category || []).slice(0, 10).map(c => `- ${c.category}: base=${formatValue(c.base_spend || 0)}, comp=${formatValue(c.comp_spend || 0)}, delta=${c.delta_pct || 0}%`).join('\n');
  const dataContext = `Period: ${periodLabel}\nEntities: ${(d.by_entity || []).length}\nSBUs: ${(d.by_sbu || []).length}\nCategories: ${(d.by_category || []).length}\nBase Spend: ${formatValue(summary.base_spend || 0)}\nComp Spend: ${formatValue(summary.comp_spend || 0)}\nDelta: ${summary.delta_pct || 0}%\n\nCategory Comparison (top 10):\n${catLines}\n\nEntity Comparison:\n${entityLines}\n\nSBU Comparison:\n${sbuLines}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // Period banner so the report is unambiguous about which windows were compared.
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(`Comparison Period: ${periodLabel}`, 14, y);
  y += 6;
  doc.setTextColor(0, 0, 0);

  autoTable(doc, {
    startY: y,
    head: [['Entities (Country Codes)', 'Strategic Business Units', 'Base Spend', 'Comp Spend', 'Delta']],
    body: [[
      String((d.by_entity || []).length),
      String((d.by_sbu || []).length),
      formatValue(summary.base_spend || 0),
      formatValue(summary.comp_spend || 0),
      `${summary.delta_pct || 0}%`,
    ]],
    styles: { fontSize: 10, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // By Category — primary YoY savings/inflation lever, mirrors the UI's first tab.
  if (d.by_category?.length > 0) {
    const top = d.by_category.slice(0, 15);
    const chartData = top.map(c => ({ label: c.category, value: c.base_spend || 0 }));
    y = drawBarChart(doc, 14, y, pw - 28, 70, chartData, { title: 'Category Spend (Base period, top 15)' });

    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Category', 'Base Spend', 'Comp Spend', 'Delta', 'Delta %']],
      body: top.map(c => [
        c.category || 'Unknown',
        formatValue(c.base_spend || 0),
        formatValue(c.comp_spend || 0),
        `${(c.delta || 0) >= 0 ? '+' : ''}${formatValue(Math.abs(c.delta || 0))}`,
        `${(c.delta_pct || 0).toFixed(1)}%`,
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  if (d.by_entity?.length > 0) {
    const chartData = d.by_entity.map(e => ({ label: e.entity, value: e.base_spend || 0 }));
    y = drawBarChart(doc, 14, y, pw - 28, 70, chartData, { title: 'Entity Spend (Country Codes)' });

    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Entity', 'Base Spend', 'Comp Spend', 'Delta %']],
      body: d.by_entity.map(e => [
        e.entity,
        formatValue(e.base_spend || 0),
        formatValue(e.comp_spend || 0),
        `${(e.delta_pct || 0).toFixed(1)}%`,
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  if (d.by_sbu?.length > 0) {
    if (y > 180) { doc.addPage(); y = 15; }
    const chartData = d.by_sbu.map(s => ({ label: s.sbu, value: s.base_spend || 0 }));
    y = drawBarChart(doc, 14, y, pw - 28, 50, chartData, { title: 'SBU Spend' });

    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['SBU', 'Base Spend', 'Comp Spend', 'Delta %']],
      body: d.by_sbu.map(s => [
        s.sbu,
        formatValue(s.base_spend || 0),
        formatValue(s.comp_spend || 0),
        `${(s.delta_pct || 0).toFixed(1)}%`,
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
